/**
 * Ico — inline SVG icon set ported from the TNSC reference.
 *
 * All icons inherit colour via `currentColor`. One file so adding/renaming
 * is a one-line edit. Keep the set tight (~15 icons in v1) — split per-domain
 * if it grows past ~30.
 */

import React from 'react';

export type IcoName =
	| 'shield'
	| 'wallet'
	| 'coins'
	| 'users'
	| 'home'
	| 'book'
	| 'search'
	| 'bell'
	| 'gear'
	| 'refresh'
	| 'download'
	| 'logout'
	| 'check'
	| 'close'
	| 'warning'
	| 'info'
	| 'arrowUp'
	| 'arrowDown'
	| 'filter'
	| 'chevron-down';

interface Props {
	name: IcoName;
	size?: number;
	className?: string;
}

const paths: Record<IcoName, React.ReactNode> = {
	shield: (
		<path
			d="M12 2l8 3v7c0 5-4 9-8 10-4-1-8-5-8-10V5l8-3z"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	),
	wallet: (
		<path
			d="M3 7h16v10H3zM3 7l4-3h10l2 3M16 12h3"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	),
	coins: (
		<g fill="none" stroke="currentColor" strokeWidth="1.5">
			<ellipse cx="9" cy="8" rx="6" ry="3" />
			<path d="M3 8v8c0 1.5 3 3 6 3s6-1.5 6-3" />
			<ellipse cx="15" cy="16" rx="6" ry="3" />
			<path d="M9 16c0 1.5 3 3 6 3" />
		</g>
	),
	users: (
		<g fill="none" stroke="currentColor" strokeWidth="1.5">
			<circle cx="9" cy="8" r="3" />
			<path d="M3 19c0-3 3-5 6-5s6 2 6 5" />
			<circle cx="17" cy="9" r="2.5" />
			<path d="M15 19c0-2 2-4 5-4" />
		</g>
	),
	home: (
		<path
			d="M3 11l9-8 9 8v10a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1V11z"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	),
	book: (
		<path
			d="M4 4h9a3 3 0 0 1 3 3v13H7a3 3 0 0 0-3 3V4zM4 4v18"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	),
	search: (
		<g fill="none" stroke="currentColor" strokeWidth="1.5">
			<circle cx="11" cy="11" r="6" />
			<path d="M16 16l4 4" />
		</g>
	),
	bell: (
		<path
			d="M6 10a6 6 0 1 1 12 0v4l2 3H4l2-3v-4zM10 20h4"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	),
	gear: (
		<g fill="none" stroke="currentColor" strokeWidth="1.5">
			<circle cx="12" cy="12" r="3" />
			<path d="M19.4 15a7.5 7.5 0 0 0 0-6l2-1.5-2-3.5-2.4.8a7.5 7.5 0 0 0-5.2-3L11 0h-4l-.8 2.3A7.5 7.5 0 0 0 6 5.3L3.6 4.5 1.6 8 3.6 9.5a7.5 7.5 0 0 0 0 5L1.6 16l2 3.5L6 18.7a7.5 7.5 0 0 0 5.2 3l.8 2.3h4l.8-2.3a7.5 7.5 0 0 0 3-3l2.4.8 2-3.5z" />
		</g>
	),
	refresh: (
		<path
			d="M4 12a8 8 0 0 1 14-5V5m2 7a8 8 0 0 1-14 5v2"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
		/>
	),
	download: (
		<path
			d="M12 4v12m0 0l-5-5m5 5l5-5M4 20h16"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	),
	logout: (
		<path
			d="M15 4h4v16h-4M3 12h13m0 0l-4-4m4 4l-4 4"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	),
	check: (
		<path
			d="M4 12l5 5L20 6"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinejoin="round"
			strokeLinecap="round"
		/>
	),
	close: (
		<path
			d="M5 5l14 14M19 5L5 19"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
		/>
	),
	warning: (
		<path
			d="M12 3l10 18H2L12 3zM12 10v4M12 17v.01"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
			strokeLinecap="round"
		/>
	),
	info: (
		<g fill="none" stroke="currentColor" strokeWidth="1.5">
			<circle cx="12" cy="12" r="9" />
			<path d="M12 8v0.01M12 11v6" />
		</g>
	),
	arrowUp: (
		<path
			d="M12 20V4m0 0l-5 5m5-5l5 5"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	),
	arrowDown: (
		<path
			d="M12 4v16m0 0l-5-5m5 5l5-5"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	),
	filter: (
		<path
			d="M3 5h18l-7 9v6l-4-2v-4L3 5z"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinejoin="round"
		/>
	),
	'chevron-down': (
		<path
			d="M6 9l6 6 6-6"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	),
};

export default function Ico({ name, size = 16, className }: Props) {
	const content = paths[name];
	if (!content) return null;
	return (
		<svg
			width={size}
			height={size}
			viewBox="0 0 24 24"
			className={className}
			role="img"
			aria-label={name}
		>
			{content}
		</svg>
	);
}
